import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import './Form.css';
import { toFullWidthKatakana, furiganaToFullWidth } from '../lib/kanaNormalize';
import { requiredFieldClass, requiredFileWrapClass } from '../lib/requiredFieldClass';
import { applicationSuccessMessage } from '../lib/applicationSuccessMessage';

import {
    fetchAddressFromZipcode,
    formatZipcode,
    lookupZipCandidates,
} from '../lib/postalAddress';
import { searchBanks, searchBranches } from '../lib/bankSearch';

const DRAFT_KEY = 'draft_joined';

/** 定期代金の入力欄 + 「円」表示 */
const FareInput = ({ id, name, className, value, onChange, required, placeholder }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input
            type="text"
            id={id}
            name={name}
            className={className}
            value={value}
            onChange={onChange}
            inputMode="numeric"
            pattern="[0-9]*"
            required={required}
            placeholder={placeholder}
            style={{ flex: 1 }}
        />
        <span>円</span>
    </div>
);

const JoinedForm = ({ onBack, onNext, isFirstForm, isLastForm, isMultiForm, id, isDraft }) => {
    const [formData, setFormData] = useState({
        startDate: '',
        name: '',
        nameFuri: '',
        birthDate: '',
        zipCode: '',
        address: '',
        contact1: '',
        contact2: '',
        bankName: '',
        branchName: '',
        branchNameKana: '',
        accountNumber: '',
        transport: '',
        road: '',
        station: '',
        fare: '',
        myNumber: '',
        image: '',
    });
    // 追加された利用機関（2件目以降）
    const [extraTrains, setExtraTrains] = useState([]);

    // 住所候補（住所→郵便番号 逆引き）
    const [addressZipCandidates, setAddressZipCandidates] = useState([]);
    const [zipSearching, setZipSearching] = useState(false);
    const addressRef = useRef(null);

    // 銀行・支店検索
    const [bankQuery, setBankQuery] = useState('');
    const [bankOptions, setBankOptions] = useState([]);
    const [selectedBankCode, setSelectedBankCode] = useState('');
    const [branchQuery, setBranchQuery] = useState('');
    const [branchOptions, setBranchOptions] = useState([]);

    // マイナンバーは formData.myNumber で管理し、画像ファイルのみ別管理
    const [imageFile, setImageFile] = useState(null);

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        try {
            const saved = localStorage.getItem(DRAFT_KEY);
            if (saved) {
                const parsed = JSON.parse(saved) || {};
                // 以前の下書きに残っているマイナンバーは読み込まずに消す（docs/spec_mynumber.md 9 章）
                if ('myNumber' in parsed) {
                    delete parsed.myNumber;
                    localStorage.setItem(DRAFT_KEY, JSON.stringify(parsed));
                }
                const { extraTrains: savedExtras, ...rest } = parsed;
                setFormData((prev) => ({ ...prev, ...rest }));
                if (Array.isArray(savedExtras)) setExtraTrains(savedExtras);
            }
        } catch (_) {}
    }, []);

    /* ===== 利用機関（追加分） ===== */
    const handleAddTrain = () => {
        setExtraTrains((prev) => [...prev, { transport: '', road: '', station: '', fare: '' }]);
    };
    const handleRemoveTrain = (index) => {
        setExtraTrains((prev) => prev.filter((_, i) => i !== index));
    };
    const handleExtraTrainChange = (index, field, rawValue) => {
        let value = rawValue;
        if (field === 'fare') {
            value = String(value || '').replace(/\D/g, '').slice(0, 8);
        }
        setExtraTrains((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    /* ===== 郵便番号 / 住所 ===== */
    const applyZipCode = useCallback(async (zipRaw) => {
        const formatted = formatZipcode(zipRaw);
        const result = await fetchAddressFromZipcode(formatted);
        if (result) {
            setFormData((prev) => ({
                ...prev,
                zipCode: formatted,
                address: result.addressKanji || prev.address,
            }));
        } else {
            setFormData((prev) => ({ ...prev, zipCode: formatted }));
        }
        setAddressZipCandidates([]);
    }, []);

    const handleSearchZipButton = async () => {
        const addr = formData.address;
        if (!addr || addr.trim().length < 4) {
            alert('住所を入力してから検索してください。');
            return;
        }
        setZipSearching(true);
        try {
            const candidates = await lookupZipCandidates(addr);
            if (candidates.length === 0) {
                alert('該当する郵便番号が見つかりませんでした。');
                setAddressZipCandidates([]);
            } else if (candidates.length === 1) {
                await applyZipCode(candidates[0].zip);
            } else {
                setAddressZipCandidates(candidates);
            }
        } finally {
            setZipSearching(false);
        }
    };

    /* ===== 銀行検索 ===== */
    const fetchBanks = useCallback(async (q) => {
        setBankOptions(await searchBanks(q));
    }, []);
    const fetchBranches = useCallback(async (bankCode, q) => {
        setBranchOptions(await searchBranches(bankCode, q));
    }, []);

    useEffect(() => {
        const id = setTimeout(() => fetchBanks(bankQuery), 320);
        return () => clearTimeout(id);
    }, [bankQuery, fetchBanks]);

    useEffect(() => {
        const id = setTimeout(() => fetchBranches(selectedBankCode, branchQuery), 320);
        return () => clearTimeout(id);
    }, [selectedBankCode, branchQuery, fetchBranches]);

    const selectBank = (b) => {
        const disp = b.normalize?.name || b.name || '';
        setFormData((prev) => ({
            ...prev,
            bankName: disp,
            branchName: '',
            branchNameKana: '',
        }));
        setSelectedBankCode(b.code || '');
        setBankOptions([]);
        setBranchQuery('');
        setBranchOptions([]);
    };

    const selectBranch = (br) => {
        const name = br.normalize?.name || br.name || '';
        const kana = br.normalize?.kana || br.kana || '';
        setFormData((prev) => ({
            ...prev,
            branchName: name,
            branchNameKana: toFullWidthKatakana(furiganaToFullWidth(kana)),
        }));
        setBranchOptions([]);
    };

    /* ===== 入力ハンドラ ===== */
    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        if (name === 'nameFuri' || name === 'branchNameKana') {
            setFormData((prev) => ({
                ...prev,
                [name]: toFullWidthKatakana(furiganaToFullWidth(value)),
            }));
            return;
        }
        if (name === 'fare') {
            const digits = value.replace(/\D/g, '').slice(0, 8);
            setFormData((prev) => ({ ...prev, fare: digits }));
            return;
        }
        if (name === 'contact1' || name === 'contact2') {
            // 連絡先は数字とハイフンのみ許容
            const cleaned = value.replace(/[^\d-]/g, '').slice(0, 15);
            setFormData((prev) => ({ ...prev, [name]: cleaned }));
            return;
        }
        if (name === 'zipCode') {
            const formatted = formatZipcode(value);
            setFormData((prev) => ({ ...prev, zipCode: formatted }));
            if (formatted.replace(/\D/g, '').length === 7) {
                const result = await fetchAddressFromZipcode(formatted);
                if (result) {
                    setFormData((prev) => ({
                        ...prev,
                        zipCode: formatted,
                        address: result.addressKanji,
                    }));
                    setTimeout(() => {
                        if (addressRef.current && typeof addressRef.current.focus === 'function') {
                            addressRef.current.focus();
                        }
                    }, 100);
                }
            }
            return;
        }
        if (name === 'myNumber') {
            const digitsOnly = value.replace(/\D/g, '').slice(0, 12);
            setFormData((prev) => ({ ...prev, myNumber: digitsOnly }));
            return;
        }
        
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setImageFile(e.target.files[0]);
    };

    const handleAddressBlur = async (e) => {
        const addr = e.target.value;
        if (!addr || addr.trim().length < 5) return;
        // 既に郵便番号が入力済みの場合は逆引きしない
        if (formData.zipCode && formData.zipCode.replace(/\D/g, '').length === 7) return;
        const candidates = await lookupZipCandidates(addr);
        if (candidates.length === 1) {
            await applyZipCode(candidates[0].zip);
        } else if (candidates.length > 1) {
            setAddressZipCandidates(candidates);
        }
    };

    const handleSaveDraft = () => {
        // マイナンバーは端末（localStorage）に保存しない（docs/spec_mynumber.md 9 章）
        const { myNumber, ...draft } = formData;
        localStorage.setItem(
            DRAFT_KEY,
            JSON.stringify({ ...draft, extraTrains })
        );
        alert('一時保存しました。（マイナンバーと画像は安全のため保存されません）');
    };

    const handleDeleteDraft = async () => {
        const confirmDelete = window.confirm("この下書き（一時保存データ）を削除してもよろしいですか？\n※削除すると元に戻せません。");
        if (!confirmDelete) return;

        setLoading(true);
        try {
            if (id) {
                await api.delete(`/api/applications/drafts/${id}/`);
            }
            // ローカルでも削除
            localStorage.removeItem(DRAFT_KEY);
            alert('下書きを削除しました。');
            navigate('/applications');
        } catch (error) {
            console.error('下書きの削除に失敗:', error);
            alert('下書きの削除に失敗しました。もう一度お試しください。');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (onBack) onBack();
        else navigate('/applications');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!window.confirm('入社申請を送信してもよろしいですか？')) return;

        const cleanedExtraTrains = extraTrains
            .map((t) => ({
                transport: t.transport || '',
                road: t.road || '',
                station: t.station || '',
                fare: parseInt(t.fare, 10) || 0,
            }))
            .filter((t) => t.transport || t.road || t.station || t.fare);

        // マイナンバー画像を含むため multipart/form-data で送信する
        // （extraTrains は配列なので JSON 文字列にして送り、バックエンドでデコードする）
        const fd = new FormData();
        fd.append('startDate', formData.startDate);
        fd.append('name', formData.name);
        fd.append('nameFuri', formData.nameFuri);
        fd.append('birthDate', formData.birthDate);
        fd.append('zipCode', formData.zipCode);
        fd.append('address', formData.address);
        fd.append('contact1', formData.contact1);
        fd.append('contact2', formData.contact2 || '');
        fd.append('bankName', formData.bankName);
        fd.append('branchName', formData.branchName);
        fd.append('branchNameKana', formData.branchNameKana);
        fd.append('accountNumber', formData.accountNumber);
        fd.append('transport', formData.transport || '');
        fd.append('road', formData.road);
        fd.append('station', formData.station);
        fd.append('fare', parseInt(formData.fare, 10) || 0);
        fd.append('myNumber', formData.myNumber || '');
        fd.append('extraTrains', JSON.stringify(cleanedExtraTrains));
        if (imageFile) fd.append('image', imageFile);

        try {
            await api.post('/forms/submit_joined/', fd);
            localStorage.removeItem(DRAFT_KEY);
            alert(
                applicationSuccessMessage('入社申請が正常に提出されました。', [
                    '申請一覧から内容・状態を確認できます。',
                ])
            );
            navigate('/applications');
        } catch (error) {
            console.error('Submission failed:', error);
            alert('申請に失敗しました。もう一度お試しください。');
        }
    };

    const canSubmit = useMemo(() => {
        const d = formData;
        return Boolean(
            d.startDate &&
                d.name?.trim() &&
                d.nameFuri?.trim() &&
                d.birthDate &&
                d.zipCode?.trim() &&
                d.address?.trim() &&
                d.contact1?.trim() &&
                d.bankName?.trim() &&
                d.branchName?.trim() &&
                d.branchNameKana?.trim() &&
                d.accountNumber?.trim() &&
                d.road?.trim() &&
                d.station?.trim() &&
                d.fare !== '' &&
                d.fare !== undefined &&
                d.myNumber?.trim()?.length === 12 &&
                imageFile
        );
    }, [formData, imageFile]);

    return (
        <div className="container form-container">
            <h3>入社届</h3>
            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label htmlFor="startDate" className="form-label">入社日</label>
                    <input type="date" id="startDate" name="startDate" className={requiredFieldClass(formData.startDate)} value={formData.startDate} onChange={handleInputChange} required />
                </div>
                <div className="mb-3">
                    <label htmlFor="name" className="form-label">氏名</label>
                    <input type="text" id="name" name="name" className={requiredFieldClass(formData.name)} value={formData.name} onChange={handleInputChange} required />
                </div>
                <div className="mb-3">
                    <label htmlFor="nameFuri" className="form-label">フリガナ</label>
                    <input type="text" id="nameFuri" name="nameFuri" className={requiredFieldClass(formData.nameFuri)} value={formData.nameFuri} onChange={handleInputChange} required />
                </div>

                <div className="mb-3">
                    <label htmlFor="birthDate" className="form-label">生年月日</label>
                    <input type="date" id="birthDate" name="birthDate" className={requiredFieldClass(formData.birthDate)} value={formData.birthDate} onChange={handleInputChange} required />
                </div>

                <div className="mb-3">
                    <label htmlFor="zipCode" className="form-label">郵便番号</label>
                    <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        className={requiredFieldClass(formData.zipCode)}
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        placeholder="例: 150-0001"
                        inputMode="numeric"
                        required
                    />
                    <small className="form-hint">郵便番号を入力すると住所が自動入力されます。</small>
                </div>

                <div className="mb-3">
                    <label htmlFor="address" className="form-label">住所</label>
                    <input
                        ref={addressRef}
                        type="text"
                        id="address"
                        name="address"
                        className={requiredFieldClass(formData.address)}
                        value={formData.address}
                        onChange={handleInputChange}
                        onBlur={handleAddressBlur}
                        required
                    />
                    <button
                        type="button"
                        onClick={handleSearchZipButton}
                        disabled={zipSearching}
                        style={{
                            marginTop: 8, padding: '8px 14px',
                            background: zipSearching ? '#9ca3af' : '#2563eb',
                            color: '#fff', border: 'none', borderRadius: 6,
                            fontWeight: 600, fontSize: '0.9rem',
                            cursor: zipSearching ? 'default' : 'pointer',
                            width: '100%', maxWidth: 400,
                        }}
                    >
                        {zipSearching ? '🔍 検索中...' : '🔍 住所から郵便番号を検索'}
                    </button>
                    <p className="form-suggest-hint">住所を入力後、上のボタンをタップすると郵便番号を自動検索します。候補が複数のときは一覧から選んでください。</p>
                    {addressZipCandidates.length > 1 && (
                        <div className="form-suggest-box" role="listbox" aria-label="郵便番号候補">
                            {addressZipCandidates.map((c) => {
                                const formattedZip = c.zip.replace(/(\d{3})(\d{4})/, '$1-$2');
                                return (
                                    <button
                                        key={c.zip}
                                        type="button"
                                        className="form-suggest-item"
                                        onClick={() => applyZipCode(c.zip)}
                                    >
                                        〒{formattedZip} {c.label}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="mb-3">
                    <label htmlFor="contact1" className="form-label">連絡先1（携帯電話）</label>
                    <input
                        type="tel"
                        id="contact1"
                        name="contact1"
                        className={requiredFieldClass(formData.contact1)}
                        value={formData.contact1}
                        onChange={handleInputChange}
                        placeholder="例: 090-1234-5678"
                        inputMode="numeric"
                        required
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="contact2" className="form-label">連絡先2（自宅）</label>
                    <input
                        type="tel"
                        id="contact2"
                        name="contact2"
                        className="form-control"
                        value={formData.contact2}
                        onChange={handleInputChange}
                        placeholder="例: 03-1234-5678"
                        inputMode="numeric"
                    />
                </div>

                <div className="mb-3">
                    <label htmlFor="bankSearch" className="form-label">
                        銀行を検索（3文字以上・ひらがな／カタカナ）
                    </label>
                    <input
                        type="text"
                        id="bankSearch"
                        name="bankSearch"
                        className="form-control"
                        value={bankQuery}
                        onChange={(e) => setBankQuery(e.target.value)}
                        autoComplete="off"
                        placeholder="例: みずほ"
                    />
                    <p className="form-suggest-hint">
                        全国の金融機関データから検索します。候補をタップして銀行名を確定してください。
                    </p>
                    {bankOptions.length > 0 && (
                        <div className="form-suggest-box" role="listbox" aria-label="銀行候補">
                            {bankOptions.map((b) => (
                                <button
                                    key={b.code}
                                    type="button"
                                    className="form-suggest-item"
                                    onClick={() => selectBank(b)}
                                >
                                    {b.normalize?.name || b.name}{' '}
                                    <span style={{ color: '#6c757d' }}>({b.code})</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mb-3">
                    <label htmlFor="bankName" className="form-label">銀行名</label>
                    <input
                        type="text"
                        id="bankName"
                        name="bankName"
                        className={requiredFieldClass(formData.bankName)}
                        value={formData.bankName}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                <div className="mb-3">
                    <label htmlFor="branchSearch" className="form-label">
                        支店を検索（銀行確定後・3文字以上）
                    </label>
                    <input
                        type="text"
                        id="branchSearch"
                        name="branchSearch"
                        className="form-control"
                        value={branchQuery}
                        onChange={(e) => setBranchQuery(e.target.value)}
                        disabled={!selectedBankCode}
                        autoComplete="off"
                        placeholder={selectedBankCode ? '例: しぶや' : '先に銀行を候補から選択'}
                    />
                    {branchOptions.length > 0 && (
                        <div className="form-suggest-box" role="listbox" aria-label="支店候補">
                            {branchOptions.map((br, idx) => (
                                <button
                                    key={`${br.code}-${idx}`}
                                    type="button"
                                    className="form-suggest-item"
                                    onClick={() => selectBranch(br)}
                                >
                                    {br.normalize?.name || br.name}{' '}
                                    <span style={{ color: '#6c757d' }}>
                                        {br.normalize?.kana || br.kana || ''}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mb-3">
                    <label htmlFor="branchName" className="form-label">支店名</label>
                    <input type="text" id="branchName" name="branchName" className={requiredFieldClass(formData.branchName)} value={formData.branchName} onChange={handleInputChange} required />
                </div>
                <div className="mb-3">
                    <label htmlFor="branchNameKana" className="form-label">支店名（フリガナ）</label>
                    <input type="text" id="branchNameKana" name="branchNameKana" className={requiredFieldClass(formData.branchNameKana)} value={formData.branchNameKana} onChange={handleInputChange} required />
                </div>
                <div className="mb-3">
                    <label htmlFor="accountNumber" className="form-label">口座番号</label>
                    <input type="text" id="accountNumber" name="accountNumber" className={requiredFieldClass(formData.accountNumber)} value={formData.accountNumber} onChange={handleInputChange} inputMode="numeric" required />
                </div>
                {/* 利用機関1 */}
                <div className="form-section">
                    <h5 className="form-section-title">利用機関1</h5>
                    <div className="mb-3">
                        <label htmlFor="transport" className="form-label">交通機関名</label>
                        <input type="text" id="transport" name="transport" className="form-control" value={formData.transport} onChange={handleInputChange} placeholder="例: JR東日本" />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="road" className="form-label">路線名</label>
                        <input type="text" id="road" name="road" className={requiredFieldClass(formData.road)} value={formData.road} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="station" className="form-label">乗車駅名～下車駅名</label>
                        <input type="text" id="station" name="station" className={requiredFieldClass(formData.station)} value={formData.station} onChange={handleInputChange} placeholder="例: 梅田 ～ 三ノ宮" required />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="fare" className="form-label">定期代金（半角数字のみ）</label>
                        <FareInput id="fare" name="fare" className={requiredFieldClass(formData.fare)} value={formData.fare} onChange={handleInputChange} required />
                    </div>
                </div>

                {/* 追加された利用機関（2件目以降） */}
                {extraTrains.map((train, index) => (
                    <div className="form-section additional-section" key={index} style={{ borderLeft: '4px solid #17a2b8' }}>
                        <h5 className="form-section-title">利用機関{index + 2}</h5>
                        <div className="mb-3">
                            <label className="form-label">交通機関名</label>
                            <input type="text" className="form-control" value={train.transport} onChange={(e) => handleExtraTrainChange(index, 'transport', e.target.value)} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">路線名</label>
                            <input type="text" className="form-control" value={train.road} onChange={(e) => handleExtraTrainChange(index, 'road', e.target.value)} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">乗車駅名～下車駅名</label>
                            <input type="text" className="form-control" value={train.station} onChange={(e) => handleExtraTrainChange(index, 'station', e.target.value)} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">定期代金</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input type="text" className="form-control" value={train.fare} onChange={(e) => handleExtraTrainChange(index, 'fare', e.target.value)} inputMode="numeric" pattern="[0-9]*" style={{ flex: 1 }} />
                                <span>円</span>
                            </div>
                        </div>
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleRemoveTrain(index)}>削除</button>
                    </div>
                ))}

                {/* 追加ボタン */}
                <div className="mb-3">
                    <button type="button" className="btn btn-outline-info" onClick={handleAddTrain}>
                        ＋ 利用機関{extraTrains.length + 2}を追加
                    </button>
                </div>

                {/* マイナンバー */}
                <div className="mb-3">
                    <label htmlFor="myNumber" className="form-label">マイナンバー</label>
                    <input type="text" id="myNumber" name="myNumber" 
                        className={requiredFieldClass(formData.myNumber)} 
                        value={formData.myNumber} 
                        onChange={handleInputChange} 
                        maxLength="12"
                        inputMode="numeric"
                        pattern="\d{12}" placeholder="例: 123-45-6789" required
                    />
                    <div className={requiredFileWrapClass(imageFile)}>
                        <input 
                            type="file" 
                            className="form-control" 
                            accept="image/*" 
                            onChange={handleFileChange} 
                            required 
                        />
                    </div>
                    <small className="text-muted">※マイナンバーカードの表面をアップロードしてください。</small>
                </div>

                <div className="button-group">
                    <button type="submit" className="btn-submit" disabled={!canSubmit}>申請</button>
                    <button type="button" className="btn-draft" onClick={handleSaveDraft}>一時保存</button>
                    {((id && isDraft) || localStorage.getItem(DRAFT_KEY)) && <button type="button" className="btn-delete-draft" onClick={handleDeleteDraft} disabled={loading}>下書削除</button>}
                    <button type="button" className="btn-back" onClick={handleBack}>{onBack && !isFirstForm ? '前へ' : '戻る'}</button>
                    {isMultiForm && onNext && <button type="button" className="btn-nav" onClick={onNext}>{isLastForm ? '完了' : '次へ'}</button>}
                </div>
            </form>
        </div>
    );
};

export default JoinedForm;
